import { track } from '@leagueplatform/analytics';
import { Box, StackLayout } from '@leagueplatform/genesis-core';
import { NestedCampaign } from '@leagueplatform/health-journey-api';
import { captureError } from '@leagueplatform/observability';
import { ActivityList } from '#components/activity-list/activity-list.component.tsx';
import { RewardProgramGroup } from '#components/reward-program-group/reward-program-group.component.tsx';
import { RewardProgramTier } from '#components/reward-program-tier/reward-program-tier.component.tsx';

import React, { useEffect } from 'react';
import { shouldDisplayDate } from '#utils/should-display-date.util.ts';

interface RewardProgramActivitiesProps {
  rewardProgram: NestedCampaign;
  isVisible?: boolean;
}

export function RewardProgramProgressTab({
  rewardProgram,
  isVisible,
}: RewardProgramActivitiesProps) {
  const { userHealthCampaigns, campaign, userHealthActivities } = rewardProgram;

  const { displayGlobalProgress } = campaign.data;

  const campaignsArrayExists = Array.isArray(userHealthCampaigns?.data);

  const showGroups = campaignsArrayExists && displayGlobalProgress;

  const showTiers = campaignsArrayExists && !displayGlobalProgress;

  const showActivities = Array.isArray(userHealthActivities?.data);

  useEffect(() => {
    if (!showGroups && !showTiers && !showActivities) {
      captureError(
        new Error(
          `Reward program(id:${rewardProgram.id}) progress data isn't provided`,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isVisible) {
      track('program.screen_loaded.health_campaign_detail_progress', {
        health_campaign_id: rewardProgram.campaignId || '',
        health_campaign_name: campaign.data.name || '',
        health_campaign_status: rewardProgram.status || '',
      });
    }
  }, [
    campaign.data.name,
    campaign.data.type,
    isVisible,
    rewardProgram.campaignId,
    rewardProgram.status,
  ]);

  return (
    <StackLayout
      css={{ marginTop: '$two' }}
      horizontalAlignment="stretch"
      spacing="$two"
      divider={
        showTiers ? (
          <Box
            css={{
              height: '$one',
              width: '100%',
              background: '$interactiveActionDisabled',
            }}
          />
        ) : undefined
      }
    >
      {showTiers &&
        userHealthCampaigns?.data.map((tier) => (
          <RewardProgramTier
            key={tier.id}
            showDate={shouldDisplayDate(
              campaign.data.startDate,
              campaign.data.endDate,
              tier.campaign.data.startDate,
              tier.campaign.data.endDate,
            )}
            relatedCampaign={tier}
          />
        ))}
      {showGroups &&
        userHealthCampaigns?.data.map((group) => (
          <RewardProgramGroup
            key={group.id}
            showDate={shouldDisplayDate(
              campaign.data.startDate,
              campaign.data.endDate,
              group.campaign.data.startDate,
              group.campaign.data.endDate,
            )}
            relatedCampaign={group}
          />
        ))}

      {showActivities && <ActivityList relatedCampaign={rewardProgram} />}
    </StackLayout>
  );
}
